<template lang="pug" src="./index.pug"/>

<script>
import {mapGetters, mapState} from 'vuex';
import request from '@/utils/request';
import {localizeField} from '@/helpers/locales';
import {loadReportWaitTime} from '../../../../config';
import {downloadBlob} from "@/utils/helpers";
import uuid from "uuid";
import {transliterate} from "transliteration";

export default {
  data: () => ({
    filters: {
      chosenBrand: null,
      chosenEstablishment: null,
      chosenOrderId: null,
      chosenCompany: null,
      query: null,
      startDate: new Date().addDays(-1).format('{yyyy}-{MM}-{dd}'),
      endDate: new Date().format('{yyyy}-{MM}-{dd}'),
      startTime: '00:00',
      endTime: '23:59',
      reportId: null,
      categoryId: null
    },
    reportData: {
      header: null,
      data: [],
      total: null
    },
    limits: {
      // startDate: new Date().addMonths(-3).format('{yyyy}-{MM}-{dd}'),
      endDate: new Date().format('{yyyy}-{MM}-{dd}')
    },
    reports: [],
    categories: [],
    startDateModal: false,
    endDateModal: false,
    startTimeModal: false,
    endTimeModal: false,
    loaded: true,
    processing: false,
    pagination: {
      page: 1,
      totalPages: 1
    }
  }),
  computed: {
    ...mapGetters(['operator', 'userBrandId', 'roleName', 'userEstablishmentId']),
    ...mapState({
      establishments: s => s.data.establishments,
      brands: s => s.data.brands,
    }),
    canRunReport() {
      if (!this.report) {
        return false;
      }

      if (this.report.useBrand && this.filters.chosenBrand === null) {
        return false;
      }

      if (this.report.useEstablishment && this.filters.chosenEstablishment === null) {
        return false;
      }

      return true;
    },
    headers() {
      if (!this.report) {
        return [];
      }

      return this.report.headers.map(header => ({
        text: this.$vuetify.lang.t(`$vuetify.pages.DeliveryReports.table.${header}`),
        sortable: false,
        align: 'center'
      }));
    },
    showFilters() {
      return this.report && (
          this.report.useDates || this.report.useQuery ||
          this.report.useTimes || this.report.useEstablishment
      );
    },
    showTable() {
      return this.reportData.data.length;
    },
    report() {
      return this.reports.find(i => this.filters.reportId === i.name);
    },
    filteredReports() {
      return this.filters.categoryId ? this.reports.filter(i => i.deliveryReportCategoryId === this.filters.categoryId) : this.reports;
    },
    allowedEstablishments() {
      const filteredEstablishments = this.report.useBrand ? this.establishments.filter(i => i.brandId === this.filters.chosenBrand) : this.establishments;

      if (['admin', 'admin-delivery'].includes(this.roleName)) {
        const allEstablishmentsTranslation = this.$vuetify.lang.t(`$vuetify.pages.DeliveryReports.allEstablishments`);

        return [{id: 0, 'name:uk':  allEstablishmentsTranslation, 'name:ru': allEstablishmentsTranslation, 'name:en': allEstablishmentsTranslation}, ...filteredEstablishments];
      }

      if (this.userEstablishmentId) {
        return [filteredEstablishments.find(i => +i.id === +this.userEstablishmentId)];
      }

      return filteredEstablishments;
    },
    allowedBrands() {
      if (['admin', 'admin-delivery'].includes(this.roleName)) {
        return [{id: 0, title: this.$vuetify.lang.t(`$vuetify.pages.DeliveryReports.allBrands`)}, ...this.brands];
      }

      if (this.userBrandId) {
        return [this.brands.find(i => +i.id === +this.userBrandId)];
      }

      return this.brands;
    }
  },
  created() {
    this.loadAvailableReports();
  },
  setup() {
    return {
      localizeField
    };
  },
  methods: {
    chooseReport(id) {
      this.filters = {
        chosenBrand: null,
        chosenEstablishment: null,
        chosenOrderId: null,
        chosenCompany: null,
        query: null,
        startDate: new Date().addDays(-1).format('{yyyy}-{MM}-{dd}'),
        endDate: new Date().format('{yyyy}-{MM}-{dd}'),
        startTime: '00:00',
        endTime: '23:59',
        reportId: id,
        categoryId: this.filters.categoryId
      };
      this.clearReportForm();

      this.$store.dispatch('data/getEstablishments')
          .then(() => {
            if (this.establishments.length === 1) {
              this.filters.chosenEstablishment = this.establishments[0].id;
            }
          });
      this.$store.dispatch('data/getBrands');
    },
    chooseReportCategory(id) {
      this.filters.categoryId = id;
      this.filters.reportId = null;
      this.clearReportForm();
    },
    async loadAvailableReports() {
      if (!this.loaded) {
        return;
      }

      await request({
        url: `/delivery-reports`,
        params: {
          scoped: true
        }
      }).then(res => {
        if (res.data.error) {
          return this.$store.dispatch('alert/addMessage', {
            type: 'error',
            message: res.data.error,
            locale: false
          });
        }

        const categories = [];

        this.reports = res.data.map(i => {
            if (!categories.find(cat => cat.id === i.deliveryReportCategoryId)) {
                categories.push({id: i.deliveryReportCategoryId, translation: this.$vuetify.lang.t('$vuetify.pages.DeliveryReports.categories.' + i.categoryName)});
            }
            return {...i, translation: this.$vuetify.lang.t('$vuetify.pages.DeliveryReports.types.' + i.name)};
        });
        this.categories = categories;
      }).catch(error => {
        if (error.response && error.response.data && error.response.data.error) {
          this.$store.dispatch('alert/addMessage', {
            message: error.response.data.error
          });
        }
      }).finally(() => {
        this.loaded = false;
      });
    },

    async loadReport(page = 1, excel) {
      this.loaded = true;
      this.clearReportForm();

      if (this.processing) return;

      this.processing = true;

      await request({
        url: `/delivery-reports/${this.filters.reportId}`,
        method: 'post',
        responseType: excel ? 'blob' : 'json',
        timeout: loadReportWaitTime * 1000,
        params: {
          page, exel: excel,
          q: this.filters.query,
          ...Object.select(this.filters, ['startDate', 'endDate',
            'startTime', 'endTime', 'chosenBrand', 'chosenEstablishment', 'chosenOrderId'])
        }
      }).then(res => {
        if (res.data.error) {
          return this.$store.dispatch('alert/addMessage', {
            type: 'error',
            message: res.data.error,
            locale: false
          });
        }

          if (excel) {
            const reportName = this.report.name ? `${transliterate(this.report.name)}_${uuid()}` : uuid();

            return downloadBlob(res.data, `Report_${reportName}.xlsx`);
          }

          this.reportData = res.data;

          // this.pagination = {
          //   page: res.data.page || 1,
          //   totalPages: res.data.totalPages || 1
          // };
      }).catch(error => {
        if (error.response && error.response.data && error.response.data.error) {
          this.$store.dispatch('alert/addMessage', {
            message: error.response.data.error
          });
        }
      }).finally(() => {
        this.loaded = false;
        this.processing = false;
      });
    },

    onPageChange(page) {
      if (page !== this.pagination.page) {
        this.loadReport(page);
      }
    },

    clearReportForm: function () {
      this.reportData = {
        header: null,
        data: [],
        total: null
      };
    },

    timeChange(value, type) {
      if (!value) {
        const defaults = {
          startTime: '00:00',
          endTime: '23:59'
        };

        this.filters[type] = defaults[type];
      }
    }
  }
};
</script>
